import { DOBSelect, GradYears, Height } from "./Athletes/Options";
import {
	Dispatch,
	FC,
	FormEvent,
	SetStateAction,
	useState,
} from "react";
import { addDoc, collection } from "firebase/firestore";

import FormDropdown from "./Dropdown";
import SchoolSelect from "./Athletes/SchoolSelect";
import { User } from "../../models/User";
import { authClasses } from "../../pages/Auth/authClasses";
import { db } from "../../firebase";

type RegisterProps = {
	user: User;
	fetchAthletes: () => Promise<void>;
	setRegistering: Dispatch<SetStateAction<boolean>>;
};

const RegisterAthlete: FC<RegisterProps> = ({
	user,
	fetchAthletes,
	setRegistering,
}) => {
	const { button, input, disabledButton } = authClasses;
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [season, setSeason] = useState("Season");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastname] = useState("");
	const [month, setMonth] = useState("Month");
	const [day, setDay] = useState("Day");
	const [year, setYear] = useState("Year");
	const [school, setSchool] = useState<string | null>(null);
	const [gradYear, setGradYear] = useState("HS Grad Year");
	const [email, setEmail] = useState("");
	const [feet, setFeet] = useState("Height (ft)");
	const [inches, setInches] = useState("Height (in)");
	const [shirtSize, setShirtSize] = useState("Shirt Size");
	const [shortSize, setShortSize] = useState("Shorts Size");
	const isFormInvalid =
		firstName.trim() === "" ||
		lastName.trim() === "" ||
		school === null ||
		season === "Season" ||
		month === "Month" ||
		day === "Day" ||
		year === "Year" ||
		gradYear === "HS Grad Year" ||
		feet === "Height (ft)" ||
		inches === "Height (in)" ||
		shirtSize === "Shirt Size" ||
		shortSize === "Shorts Size";

	const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isFormInvalid) {
			setError("Please complete all required fields before saving.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			await addDoc(collection(db, "users", user.id, "athletes"), {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				school,
				gradYear,
				email: email.trim(),
				feet,
				inches,
				shirtSize,
				shortSize,
				season,
				dob: new Date(`${month} ${day}, ${year}`),
			});
			await fetchAthletes();
			setRegistering(false);
		} catch {
			setError("We could not save this athlete. Please try again.");
			setLoading(false);
		}
	};

	return (
		<section className="mx-auto grid w-full max-w-3xl gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-4 shadow-2xl shadow-black/20 md:p-6">
			<div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
				<div>
					<h2 className="m-0 text-2xl text-white">Add Child Athlete</h2>
					<p className="m-0 mt-1 text-sm text-slate-400">
						Add a child athlete for club tryouts, clinics, and Hatchlings
						events. Adult league players will register themselves and do not
						need a child profile.
					</p>
				</div>
				<button
					className="min-h-[46px] rounded-md border border-white/10 bg-black/20 px-4 py-2 font-bold text-slate-200 transition hover:border-accent-color-2 hover:text-accent-color-2"
					type="button"
					onClick={() => setRegistering(false)}
				>
					Cancel
				</button>
			</div>

			<form onSubmit={handleFormSubmit} className="grid gap-5">
				{error ? (
					<div className="rounded-md border border-red-300/30 bg-red-500/15 p-3 text-sm text-red-100">
						{error}
					</div>
				) : null}

				<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-4">
					<h3 className="m-0 text-lg text-white">Basic Info</h3>
					<div className="grid gap-3 md:grid-cols-2">
						<input
							type="text"
							placeholder="First Name"
							className={input}
							value={firstName}
							onChange={(event) => setFirstName(event.target.value)}
							required
						/>
						<input
							type="text"
							placeholder="Last Name"
							className={input}
							value={lastName}
							onChange={(event) => setLastname(event.target.value)}
							required
						/>
					</div>
					<input
						type="email"
						placeholder="Child Athlete Email (optional)"
						className={input}
						value={email}
						onChange={(event) => setEmail(event.target.value)}
					/>
				</div>

				<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-4">
					<h3 className="m-0 text-lg text-white">Date of Birth</h3>
					<DOBSelect
						day={day}
						setDay={setDay}
						month={month}
						setMonth={setMonth}
						year={year}
						setYear={setYear}
					/>
				</div>

				<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-4">
					<h3 className="m-0 text-lg text-white">School</h3>
					<div className="grid gap-3 md:grid-cols-2">
						<SchoolSelect defaultValue={school} setSchool={setSchool} />
						<FormDropdown
							options={GradYears}
							value={gradYear}
							setVal={setGradYear}
						/>
					</div>
				</div>

				<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-4">
					<h3 className="m-0 text-lg text-white">Child Athlete Details</h3>
					<div className="grid gap-3 md:grid-cols-2">
						<div className="grid gap-2">
							<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
								Height
							</span>
							<Height
								feet={feet}
								setFeet={setFeet}
								inches={inches}
								setInches={setInches}
							/>
						</div>

						<div className="grid gap-2">
							<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
								Season
							</span>
							<FormDropdown
								options={["Season", "Boys", "Girls"]}
								value={season}
								setVal={setSeason}
							/>
						</div>

						<div className="grid gap-2">
							<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
								Shirt Size
							</span>
							<FormDropdown
								options={["Shirt Size", "S", "M", "L", "XL", "2XL"]}
								value={shirtSize}
								setVal={setShirtSize}
							/>
						</div>

						<div className="grid gap-2">
							<span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
								{season === "Girls" ? "Spandex/Legging Size" : "Shorts Size"}
							</span>
							<FormDropdown
								options={["Shorts Size", "S", "M", "L", "XL", "2XL"]}
								value={shortSize}
								setVal={setShortSize}
							/>
						</div>
					</div>
				</div>

				<div className="flex flex-wrap justify-end gap-3">
					<button
						className="min-h-[46px] rounded-md border border-white/10 bg-black/20 px-4 py-2 font-bold text-slate-200 transition hover:border-accent-color-2 hover:text-accent-color-2"
						type="button"
						onClick={() => setRegistering(false)}
					>
						Cancel
					</button>
					<button
						disabled={loading || isFormInvalid}
						className={loading || isFormInvalid ? disabledButton : button}
					>
						{loading ? "Saving..." : "Save Child Athlete"}
					</button>
				</div>
			</form>
		</section>
	);
};

export default RegisterAthlete;
